import * as S from './styles';
import { useCart, useTables } from '@/hooks';
import Modal from '../modal/Modal';
import { colors } from '@/constants';
import { RiInformationLine } from 'react-icons/ri';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { ProductProps, Attributes, PackagesTypesProps, PriceProductProps } from '@/types';
import { ProductImage } from '@/components';
import * as Packing from '@/components/packaging/styles';
import { ATTRIBUTES_LABELS, packingTypes } from '@/shared';
import PackagingAttributesTable from '@/views/product-detail/pack-attributes/PackAttributeTable';

interface ModalCustomAttributesProps {
  isOpen: boolean;
  product: ProductProps | null;
  packQuantity: number;
  pack: PackagesTypesProps | null;
  openModal: Dispatch<SetStateAction<boolean>>;
  price: PriceProductProps | null;
}

const ModalCustomAttributes = ({ product, pack, packQuantity, openModal, isOpen = true, price }: ModalCustomAttributesProps) => {
  const { getStockMsg } = useCart();
  const [currentIsOpen, setCurrentIsOpen] = useState(false);
  const [selectedPack, setSelectedPack] = useState<PackagesTypesProps | null>(pack);

  useEffect(() => {
    setTimeout(() => {
      setSelectedPack(pack || null);
      setCurrentIsOpen(isOpen);
    }, 300);
  }, [isOpen]);

  const hasExpiring = useMemo(() => {
    if (!product?.COD_PRODUTO) return false;
    return getStockMsg(product?.COD_PRODUTO);
  }, [getStockMsg, product?.COD_PRODUTO]);

  const expiringSeverity = useMemo<'danger' | 'warning' | 'normal'>(() => {
    const msg = hasExpiring || '';
    const match = msg.match(/(\d+)/);
    const days = match ? Number(match[1]) : NaN;
    if (!isNaN(days)) {
      if (days <= 90) return 'danger';
      if (days <= 120) return 'warning';
    }
    return 'normal';
  }, [hasExpiring]);

  const dun = useMemo(() => {
    return product?.EMBALAGEM?.find((i) => i.SG_EMBALAGEM === pack && i.QTD_EMBALAGEM * i.QTD_MULTIPLO_VENDA === packQuantity)
      ?.DUN_PRODUTO;
  }, [product, pack, packQuantity]);

  let ean = { ean_grp: String(product?.EMBALAGEM?.find(emb => emb?.EAN_PRODUTO)?.EAN_PRODUTO ?? '') || '' };

  const atributos = useMemo(() => {
    if (!product?.ATRIBUTOS) return [];

    const list = product?.ATRIBUTOS?.filter((i) => {
      const key = Object.keys(i)?.[0] as Attributes;
      const isValid = i?.[key] !== null;
      if (key === 'ean_grp' && isValid) ean = { ean_grp: i?.[key]! };
      return isValid && key !== 'ean_grp';
    });

    if (!!price?.COD_PROMOCAO) list.unshift({ cod_promocao: `${price.COD_PROMOCAO}` });
    if (dun && dun.toString() !== '0') list.unshift({ dun_grp: dun.toString() });
    list.unshift(ean);

    return list;
  }, [product, dun]);

  const orderedPackaging = useMemo(() => {
    const list = [...(product?.EMBALAGEM || [])]?.sort((a, b) => a?.QTD_EMBALAGEM * a?.QTD_MULTIPLO_VENDA - b?.QTD_EMBALAGEM * b?.QTD_MULTIPLO_VENDA).reverse();
    return list;
  }, [product]);


  const selectedPackagingAttributes = useMemo(() => {
    const selectedPackaging = orderedPackaging.find((emb) => emb?.SG_EMBALAGEM === selectedPack);

    return [
      { MD_ALTURA: selectedPackaging?.MD_ALTURA },
      { MD_LARGURA: selectedPackaging?.MD_LARGURA },
      { MD_PROFUNDIDADE: selectedPackaging?.MD_PROFUNDIDADE },
      { MD_CUBAGEM: selectedPackaging?.MD_CUBAGEM },
      { QTD_CX_CAMADA: selectedPackaging?.QTD_CX_CAMADA },
      { QTD_CX_PALETE: selectedPackaging?.QTD_CX_PALETE },
      { MD_PESO_LIQUIDO: selectedPackaging?.MD_PESO_LIQUIDO },
      { MD_PESO_BRUTO: selectedPackaging?.MD_PESO_BRUTO },
    ]
  }, [orderedPackaging, selectedPack]);

  return (
    <Modal
      id={`modal-custom-attributes-${product?.COD_PRODUTO ?? ''}`}
      direction="right"
      isOpen={currentIsOpen}
      isScrollable={false}
      closeModal={openModal}
    >
      {currentIsOpen && product && pack && price && (
        <>
          <S.Title>Detalhes do produto</S.Title>
          <S.Divider />
          <S.ModalContent>
            <S.ProductIdGrid>
              <S.ProductImageWrapper $hasExpiring={!!hasExpiring}>
                <S.ProductImage>
                  <ProductImage COD_PRODUTO={product.COD_PRODUTO} />
                </S.ProductImage>
              </S.ProductImageWrapper>
              <S.ProductName $hasExpiring={!!hasExpiring} $severity={expiringSeverity}>
                {product.DESC_PRODUTO_BUSCA || product.DESC_PRODUTO}
              </S.ProductName>
            </S.ProductIdGrid>

            {hasExpiring && (
              <S.ExpiringView>
                <RiInformationLine size={18} color={expiringSeverity === 'danger' ? colors.alert.red200 : colors.alert.orange300} />

                <S.ExpiringText $severity={expiringSeverity}>{hasExpiring}</S.ExpiringText>
              </S.ExpiringView>
            )}

            <S.ProductTable>
              <S.TableBody>
                {atributos?.map((item, index) => {
                  const key = Object.keys(item)?.[0] as Attributes;
                  const value = item[key];
                  if (key && value) {
                    if (!!ATTRIBUTES_LABELS[key as keyof typeof ATTRIBUTES_LABELS]) {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'}>{ATTRIBUTES_LABELS[key as keyof typeof ATTRIBUTES_LABELS]}</S.TableCell>
                          <S.TableCell dangerouslySetInnerHTML={{ __html: value }} />
                        </S.TableRow>
                      );
                    } else {
                      return (
                        <S.TableRow key={`${key}-${index}`} $applyBackground={(index + 1) % 2 !== 0}>
                          <S.TableCell $bold={'true'} $capitalize={true}>
                            {key?.split('_')?.[0]}
                          </S.TableCell>
                          <S.TableCell>{value}</S.TableCell>
                        </S.TableRow>
                      );
                    }
                  }
                  return null;
                })}
              </S.TableBody>
            
              <PackagingAttributesTable product={product} pack={pack} />
            </S.ProductTable>

          </S.ModalContent>
        </>
      )}
    </Modal>
  );
};

export default ModalCustomAttributes;
